import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import DrawerWithHeader from 'components/UI/DrawerWithHeader/DrawerWithHeader';
import DeleteDialog from 'components/UI/Dialogs/DeleteDialog';
import Spinner from 'components/UI/Spinner';
import { useAppSelector } from 'src/app/hooks';
import { selectProformaInvoices } from 'src/slices/proformaInvoicesSlice/proformaInvoicesSlice';

import CreateForm from '../ProformaInvoiceForm/CreateForm/CreateForm';
import UpdateForm from '../ProformaInvoiceForm/UpdateForm/UpdateForm';
import { ProformaInvoiceDraft } from 'src/types/proformaInvoice';
import ProformaInvoiceEntity from 'src/db/entities/proforma_invoice/ProformaInvoiceEntity';
import styles from './InvoiceDrawer.module.css';

export type ProformaInvoiceDrawerMode = 'create' | 'update' | null;

interface CreateOptions {
  defaultValues?: Partial<ProformaInvoiceDraft>;
  onSuccess?: () => void;
}

interface UpdateOptions {
  // null while the invoice is being fetched
  invoice: ProformaInvoiceEntity | null;
  onSuccess?: () => void;
}

interface ProformaInvoiceDrawerProps {
  mode: ProformaInvoiceDrawerMode;
  createOptions?: CreateOptions;
  updateOptions?: UpdateOptions;
  onClose: () => void;
}

export default function InvoiceDrawer({
  mode,
  createOptions,
  updateOptions,
  onClose,
}: ProformaInvoiceDrawerProps) {
  const { t } = useTranslation('proforma-invoices-page');

  // The drawer opens with the invoice fetched from the server. Payments change it on the
  // server (totals, status), and the answer lands in the local list, so follow that copy.
  const openedInvoice = updateOptions?.invoice ?? null;
  const storedInvoice = useAppSelector((state) =>
    openedInvoice ? selectProformaInvoices(state).find((i) => i.uuid === openedInvoice.uuid) : null
  );
  const invoice = storedInvoice ?? openedInvoice;

  const [formDirty, setFormDirty] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    setFormDirty(false);
    setConfirmCloseOpen(false);
  }, [mode]);

  const handleFormDataChanged = useCallback(() => {
    setFormDirty(true);
  }, []);

  const handleClose = () => {
    if (formDirty) {
      setConfirmCloseOpen(true);
    } else {
      onClose();
    }
  };

  const titleKey = mode === 'create' ? 'form.title.create' : 'form.title.update';

  return (
    <DrawerWithHeader title={t(titleKey)} open={mode !== null} onClose={handleClose} anchor="right">
      {mode === 'create' && (
        <CreateForm
          defaultValues={createOptions?.defaultValues}
          onSuccess={createOptions?.onSuccess}
          onCancel={handleClose}
          onFormDataChanged={handleFormDataChanged}
        />
      )}
      {mode === 'update' && !invoice && (
        <div className={styles.loader}>
          <Spinner />
        </div>
      )}
      {mode === 'update' && invoice && (
        <UpdateForm
          key={invoice.uuid}
          invoice={invoice}
          onSuccess={updateOptions.onSuccess}
          onCancel={handleClose}
          onFormDataChanged={handleFormDataChanged}
        />
      )}

      {confirmCloseOpen && (
        <DeleteDialog
          title={t('form.closeConfirm.title')}
          description={t('form.closeConfirm.description')}
          open={confirmCloseOpen}
          onClose={() => setConfirmCloseOpen(false)}
          onSuccess={() => {
            setConfirmCloseOpen(false);
            onClose();
          }}
          cancelButtonText={t('form.closeConfirm.cancel')}
          deleteButtonText={t('form.closeConfirm.discard')}
        />
      )}
    </DrawerWithHeader>
  );
}
